import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import {
  handleNewsletterPageFeedback,
  initNewsletterSubmitTracking,
  isCustomerPosted,
  peekSubmitSource,
  shouldShowPopupSuccessForSource,
  SUBMIT_SOURCE_KEY,
  NewsletterSubmitSource,
} from '@theme/topeak-newsletter-forms';
import { submitShopifyProtectedForm } from '@theme/topeak-shopify-captcha';

// const STORAGE_KEY = 'topeak-newsletter-popup-dismissed';

/**
 * Newsletter signup popup — shows on each home page visit (dismissal storage disabled for testing).
 *
 * @extends {DialogComponent}
 */
class TopeakNewsletterPopup extends DialogComponent {
  #abortController = new AbortController();
  #showTimeoutId = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
  #successActivated = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.hasAttribute('disabled')) return;

    initNewsletterSubmitTracking();

    const { signal } = this.#abortController;

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDismiss, { signal });
    this.addEventListener('topeak-newsletter:show-success', this.#handleShowSuccess, { signal });
    this.addEventListener('topeak-newsletter:show-error', this.#handleShowError, { signal });
    this.querySelector('[data-newsletter-dismiss]')?.addEventListener('click', this.#handleDismissClick, { signal });
    this.querySelector('.topeak-newsletter-popup__form')?.addEventListener('submit', this.#handleFormSubmit, { signal });

    const pendingSource = peekSubmitSource();
    const pendingSuccess =
      isCustomerPosted() && shouldShowPopupSuccessForSource(pendingSource);

    if (!pendingSuccess) {
      this.#resetToFormView();
    }

    handleNewsletterPageFeedback(this);

    if (this.#shouldSkip() && !this.#hasFormError() && !this.#hasFormSuccess()) return;

    const delayMs = this.#hasFormError() || this.#hasFormSuccess() ? 0 : Number(this.dataset.delayMs) || 0;
    this.#showTimeoutId = setTimeout(() => {
      this.#showTimeoutId = null;
      if (this.#shouldSkip() && !this.#hasFormError() && !this.#hasFormSuccess()) return;
      this.showDialog();
      this.#focusAnnouncement();
    }, delayMs);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    if (this.#showTimeoutId) {
      clearTimeout(this.#showTimeoutId);
      this.#showTimeoutId = null;
    }
  }

  #hasFormError() {
    return Boolean(
      this.querySelector('.topeak-newsletter-popup__message--error:not([hidden])') ||
        this.querySelector('.topeak-newsletter-popup__input[aria-invalid="true"]')
    );
  }

  #hasFormSuccess() {
    return this.#successActivated || Boolean(this.querySelector('.topeak-newsletter-popup__content--success'));
  }

  /**
   * @returns {boolean}
   */
  #shouldSkip() {
    // Dismissal persistence disabled — popup shows on every visit.
    // if (this.#hasFormSuccess()) {
    //   this.#persistDismissed();
    //   return false;
    // }
    //
    // try {
    //   if (window.localStorage.getItem(STORAGE_KEY) === 'true') return true;
    // } catch {
    //   return false;
    // }

    return false;
  }

  #persistDismissed = () => {
    // Dismissal persistence disabled — popup shows on every visit.
    // try {
    //   window.localStorage.setItem(STORAGE_KEY, 'true');
    // } catch {
    //   /* ignore storage failures */
    // }
  };

  #handleDismiss = () => {
    // this.#persistDismissed();
  };

  #handleDismissClick = (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleFormSubmit = (/** @type {SubmitEvent} */ event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();

    submitShopifyProtectedForm(form, () => {
      try {
        sessionStorage.setItem(SUBMIT_SOURCE_KEY, NewsletterSubmitSource.POPUP);
      } catch {
        /* ignore storage failures */
      }

      const submit = form.querySelector('.topeak-newsletter-popup__submit');
      const submitText = form.querySelector('.topeak-newsletter-popup__submit-text');

      if (!(submit instanceof HTMLButtonElement) || !(submitText instanceof HTMLElement)) return;

      submit.setAttribute('aria-busy', 'true');
      submit.classList.add('topeak-btn--loading');
      submitText.textContent = submit.dataset.loadingLabel || 'Subscribing...';
    });
  };

  #handleShowSuccess = () => {
    this.#activateSuccessView();
    // this.#persistDismissed();

    if (this.#showTimeoutId) {
      clearTimeout(this.#showTimeoutId);
      this.#showTimeoutId = null;
    }

    this.showDialog();
    this.#focusAnnouncement();
  };

  #handleShowError = () => {
    const errorMessage = this.querySelector('.topeak-newsletter-popup__message--error');
    const input = this.querySelector('.topeak-newsletter-popup__input');

    if (errorMessage instanceof HTMLElement) {
      errorMessage.hidden = false;
    }

    if (input instanceof HTMLInputElement && errorMessage instanceof HTMLElement) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', errorMessage.id);
      input.focus();
    }

    if (this.#showTimeoutId) {
      clearTimeout(this.#showTimeoutId);
      this.#showTimeoutId = null;
    }

    this.showDialog();
    this.#focusAnnouncement();
  };

  #resetToFormView() {
    const content = this.querySelector('[data-newsletter-popup-content]');
    const success = this.querySelector('[data-newsletter-popup-success]');
    const formPanel = this.querySelector('[data-newsletter-popup-form-panel]');
    const dialog = this.querySelector('dialog');
    const heading = this.querySelector('.topeak-newsletter-popup__heading');

    if (success instanceof HTMLElement) {
      success.hidden = true;
    }

    if (formPanel instanceof HTMLElement) {
      formPanel.hidden = false;
    }

    if (content instanceof HTMLElement) {
      content.classList.remove('topeak-newsletter-popup__content--success');
    }

    if (dialog instanceof HTMLElement && heading instanceof HTMLElement) {
      dialog.setAttribute('aria-labelledby', heading.id);
    }

    this.#successActivated = false;
  }

  #activateSuccessView() {
    if (this.#successActivated) return;

    const content = this.querySelector('[data-newsletter-popup-content]');
    const success = this.querySelector('[data-newsletter-popup-success]');
    const formPanel = this.querySelector('[data-newsletter-popup-form-panel]');
    const dialog = this.querySelector('dialog');
    const successHeading = this.querySelector('.topeak-newsletter-popup__success-heading');

    if (success instanceof HTMLElement) {
      success.hidden = false;
    }

    if (formPanel instanceof HTMLElement) {
      formPanel.hidden = true;
    }

    if (content instanceof HTMLElement) {
      content.classList.add('topeak-newsletter-popup__content--success');
    }

    if (dialog instanceof HTMLElement && successHeading instanceof HTMLElement) {
      dialog.setAttribute('aria-labelledby', successHeading.id);
    }

    this.#successActivated = true;
  }

  #focusAnnouncement() {
    const target =
      (this.#successActivated && this.querySelector('.topeak-newsletter-popup__success-heading')) ||
      this.querySelector('.topeak-newsletter-popup__message--error:not([hidden])') ||
      this.querySelector('.topeak-newsletter-popup__input[aria-invalid="true"]') ||
      this.querySelector('.topeak-newsletter-popup__heading');

    if (!(target instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      try {
        target.focus({ focusVisible: false, preventScroll: true });
      } catch {
        target.focus();
      }
    });
  }
}

if (!customElements.get('topeak-newsletter-popup')) {
  customElements.define('topeak-newsletter-popup', TopeakNewsletterPopup);
}
